import { Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FrameworkServiceApiClient } from '../api-clients/framework-service-api-client';
import { AuthService } from './auth.service';
import { ToastMessageService } from '../components';
import { filter, firstValueFrom, take } from 'rxjs';
import { LogService } from '@nts/std/utility';
import { MessageResourceManager } from '../resources/message-resource-manager';

@UntilDestroy()
@Injectable()
export class TokenService {

  private onSessionRefreshingNeededInProgress = false;
  private onUserProfileRefreshingNeededInProgress = false;
  private onTenantProfileRefreshingNeededInProgress = false;
  private initialized = false;
  private initPromise: Promise<void> | null = null;

  constructor(
    private authService: AuthService,
    private frameworkServiceApiClient: FrameworkServiceApiClient,
    private toastMessageService: ToastMessageService,
  ) {

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.init();
  }

  async init(): Promise<void> {

    if (this.initialized) {
      return;
    }

    if (this.initPromise != null) {
      await this.initPromise;
      return;
    }

    this.initPromise = this.doInit();

    try {
      await this.initPromise;
      this.initialized = true;
    } finally {
      this.initPromise = null;
    }
  }

  private async doInit(): Promise<void> {
    this.authService.onSessionRefreshingNeeded.pipe(
      untilDestroyed(this),
      filter(() => !this.onSessionRefreshingNeededInProgress)
    ).subscribe(

      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      async (refreshToken) => {
        this.onSessionRefreshingNeededInProgress = true;
        // Check if refresh token is valid
        if (this.authService.isTokenValid(refreshToken)) {
          const result = await firstValueFrom(this.frameworkServiceApiClient.refreshToken(refreshToken, 10));

          if (window.self === window.top) {
            this.toastMessageService.showToastsFromResponse(result);
          }

          if (result.operationSuccedeed) {
            LogService.debug('onSessionRefreshingNeeded ok', result);
            await this.authService.setRefreshToken(result.result.refresh_token);
            await this.authService.setAccessToken(result.result.access_token);
          } else {
            LogService.warn('onSessionRefreshingNeeded failed', result);
            await this.authService.clearAuthData();
            this.authService.onSessionRefreshingError.next(result);
          }
        } else { // refresh token is not valid
          LogService.warn('onSessionRefreshingNeeded failed, refreshToken is not valid!');
          await this.authService.clearAuthData();
          this.authService.onSessionRefreshingError.next(null);
        }

        this.onSessionRefreshingNeededInProgress = false;
      });

    this.authService.onSessionExpired.pipe(untilDestroyed(this), take(1)).subscribe(() => {
      const msg = MessageResourceManager.Current.getMessage('std_SessionExpired');
      alert(msg); // TODO usare il modal service
      this.authService.logIn();
    });

    this.authService.onUserProfileRefreshingNeeded
      .pipe(
        untilDestroyed(this),
        filter((need) => need === true),
        filter(() => !this.onUserProfileRefreshingNeededInProgress)
      )
      .subscribe(

        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        async (need: boolean) => {
          this.onUserProfileRefreshingNeededInProgress = true;
          this.authService.onUserProfileRefreshingNeeded.next(false);

          const isUserAuthenticated = await firstValueFrom(this.authService.isUserAuthenticated());
          if (isUserAuthenticated) {
            const result = await firstValueFrom(this.frameworkServiceApiClient.getUserProfile());
            if (result.operationSuccedeed) {
              LogService.debug('onUserProfileRefreshingNeeded ok', result);
              await this.authService.setUserProfile(result.result);
            } else {
              LogService.warn('onUserProfileRefreshingNeeded failed', result);
              this.authService.onUserProfileRefreshingError.next(result);
            }
          } else {
            LogService.warn('onUserProfileRefreshingNeeded failed: impossibile autenticarsi');
            this.authService.onUserProfileRefreshingError.next(null);
          }

          this.authService.onUserProfileRefreshingNeeded.next(false);
          this.onUserProfileRefreshingNeededInProgress = false;
        });

    this.authService.onTenantProfileRefreshingNeeded
      .pipe(
        untilDestroyed(this),
        filter((need) => need === true),
        filter(() => !this.onTenantProfileRefreshingNeededInProgress)
      )
      .subscribe(
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        async (need: boolean) => {
          this.onTenantProfileRefreshingNeededInProgress = true;
          this.authService.onTenantProfileRefreshingNeeded.next(false);
          this.authService.onTenantProfileRefreshingInProgress.next(true);

          const isUserAuthenticated = await firstValueFrom(this.authService.isUserAuthenticated());
          if (isUserAuthenticated) {
            const result = await firstValueFrom(this.frameworkServiceApiClient.getTenantProfile());
            if (result.operationSuccedeed) {
              LogService.debug('onTenantProfileRefreshingNeeded ok', result);
              await this.authService.setTenantProfile(result.result);
            } else {
              LogService.warn('onTenantProfileRefreshingNeeded failed', result);
              this.authService.onTenantProfileRefreshingError.next(result);
            }
          } else {
            LogService.warn('onTenantProfileRefreshingNeeded failed: impossibile autenticarsi');
            this.authService.onTenantProfileRefreshingError.next(null);
          }

          this.authService.onTenantProfileRefreshingNeeded.next(false);
          this.onTenantProfileRefreshingNeededInProgress = false;
          this.authService.onTenantProfileRefreshingInProgress.next(false);
        });
    return Promise.resolve();
  }
}
